import type { Table } from '@tanstack/react-table';
import { BatteryFull, BatteryWarning, BellOff, PlugZap } from 'lucide-react';
import { memo, useCallback, useEffect } from 'react';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { cn } from 'lib/utils';

export type StatusType =
  | 'needStock'
  | 'canTransfer'
  | 'noTrans'
  | 'overStock'
  | undefined;

type Props = {
  table: Table<IOneItemInPrepareForRequest>;
  wholeSaleType: StatusType;
  setWholeSaleType: React.Dispatch<React.SetStateAction<StatusType>>;
};

const statusTypesArray = [
  {
    value: 'needStock',
    label: 'يحتاج رصيد',
    bg: 'bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-200 dark:border-red-800',
    icon: <BatteryWarning className="h-4 w-4 rotate-90" />,
  },
  {
    value: 'canTransfer',
    label: 'التحويل',
    bg: 'bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-800/30',
    text: 'text-yellow-700 dark:text-yellow-300',
    border: 'border-yellow-200 dark:border-yellow-800',
    icon: <PlugZap className="h-4 w-4 rotate-90" />,
  },
  {
    value: 'noTrans',
    label: 'لا حركة',
    bg: 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900/30 dark:to-gray-800/30',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-200 dark:border-gray-800',
    icon: <BellOff className="h-4 w-4 rotate-90" />,
  },
  {
    value: 'overStock',
    label: 'رصيد زيادة',
    bg: 'bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-200 dark:border-green-800',
    icon: <BatteryFull className="h-4 w-4 rotate-90" />,
  },
];

function WholeSaleStatusFilter({
  table,
  setWholeSaleType,
  wholeSaleType,
}: Props) {
  const wholeSaleStatusColumn = table.getColumn('WholeSaleSalesStatus');

  // Ensure the column is available for filtering
  useEffect(() => {
    if (wholeSaleStatusColumn && wholeSaleType) {
      wholeSaleStatusColumn.setFilterValue(wholeSaleType);
    }
  }, [wholeSaleStatusColumn, wholeSaleType]);

  const handleStatusTypeChange = useCallback(
    (value: StatusType) => {
      setWholeSaleType(value);
      if (wholeSaleStatusColumn) {
        wholeSaleStatusColumn.setFilterValue(value);
      }
    },
    [setWholeSaleType, wholeSaleStatusColumn],
  );

  return (
    <div className="space-y-1 rounded-lg border bg-white/50 p-1.5 shadow-sm backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/50">
      <div className="flex items-center justify-between">
        <h3 className="text-2xs font-semibold text-gray-900 dark:text-gray-100">
          حالة الجملة
        </h3>
        {wholeSaleType && (
          <span className="rounded-full bg-red-100 px-1 py-0.5 text-2xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
            مفعل
          </span>
        )}
      </div>
      <ToggleGroup
        value={wholeSaleType || ''}
        onValueChange={(value) => {
          handleStatusTypeChange(value ? (value as StatusType) : undefined);
        }}
        type="single"
        className="grid grid-cols-2 gap-1"
      >
        {statusTypesArray.map((item) => (
          <ToggleGroupItem
            key={item.value}
            value={item.value}
            aria-label={item.label}
            className={cn(
              'group relative flex h-6 items-center justify-between overflow-hidden rounded-md border p-0 transition-all duration-200 hover:scale-[1.02] hover:shadow-md',
              item.border,
              item.value === wholeSaleType &&
                'border-2 border-primary shadow-sm ring-1 ring-primary/20',
            )}
          >
            <div
              className={cn(
                'flex h-full w-6 items-center justify-center border-l transition-colors duration-200',
                item.bg,
                item.border,
                item.text,
              )}
            >
              {item.icon}
            </div>
            <div className="flex-1 text-center text-[10px] font-medium text-gray-700 transition-colors duration-200 group-hover:text-gray-900 dark:text-gray-300 dark:group-hover:text-gray-100">
              {item.label}
            </div>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

export default memo(WholeSaleStatusFilter);
